<template>
  <div>
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
      <b-form-checkbox v-model="includeRevoked" switch>แสดงรายการที่ยกเลิกแล้ว</b-form-checkbox>
      <b-button size="sm" variant="outline-secondary" :disabled="isLoading" @click="load">
        <i class="fas fa-sync-alt mr-1"></i>รีเฟรช
      </b-button>
    </div>
    <b-alert v-if="errorMessage" show variant="danger">{{ errorMessage }}</b-alert>
    <b-table
      :items="items"
      :fields="fields"
      :busy="isLoading"
      small
      hover
      responsive
      show-empty
      empty-text="ยังไม่มีผู้ใช้เชื่อมต่อ AI"
    >
      <template #table-busy>
        <div class="text-center text-muted my-2">
          <b-spinner small class="align-middle mr-2"></b-spinner>
          <strong>กำลังโหลด...</strong>
        </div>
      </template>
      <template #cell(user)="row">
        <div>{{ row.item.user.name || "-" }}</div>
        <small class="text-muted">{{ row.item.user.email }}</small>
      </template>
      <template #cell(client)="row">
        <div>{{ row.item.clientName || "ไม่ระบุชื่อแอป" }}</div>
        <small class="text-muted">{{ row.item.redirectHost }}</small>
      </template>
      <template #cell(creationDt)="row">{{ formatDateTime(row.item.creationDt) }}</template>
      <template #cell(lastUsedAt)="row">{{ formatDateTime(row.item.lastUsedAt) }}</template>
      <template #cell(status)="row">
        <b-badge v-if="row.item.active" variant="success">เชื่อมต่ออยู่</b-badge>
        <div v-else>
          <b-badge variant="secondary">ยกเลิกแล้ว</b-badge>
          <small class="d-block text-muted">
            {{ formatDateTime(row.item.revokedAt) }}
            <span v-if="row.item.revokedBy">โดย {{ row.item.revokedBy.name || `uid ${row.item.revokedBy.uid}` }}</span>
          </small>
        </div>
      </template>
      <template #cell(actions)="row">
        <b-button
          v-if="row.item.active"
          size="sm"
          variant="outline-danger"
          :disabled="revokingId === row.item.id"
          @click="confirmRevoke(row.item)"
        >
          ยกเลิกการเชื่อมต่อ
        </b-button>
      </template>
    </b-table>
    <small class="text-muted">ชื่อแอป AI มาจากผู้ให้บริการที่เชื่อมต่อ ใช้เพื่อแสดงผลเท่านั้น</small>
  </div>
</template>

<script>
import moment from "moment";
import { mcpErrorMessage } from "@/components/mcp/mcpErrors";

export default {
  name: "McpConnections",
  props: {
    clinicUrl: { type: String, required: true },
  },
  data() {
    return {
      // Always fetched with includeRevoked so the emitted "loaded" list (used by
      // the audit log's user filter, ruling D7-c) covers users whose connections
      // are all revoked, not just currently-active ones. The toggle below only
      // filters what this table itself displays.
      rawItems: [],
      includeRevoked: false,
      isLoading: false,
      revokingId: null,
      errorMessage: "",
      fields: [
        { key: "user", label: "ผู้ใช้" },
        { key: "client", label: "แอป AI" },
        { key: "creationDt", label: "เชื่อมต่อเมื่อ" },
        { key: "lastUsedAt", label: "ใช้งานล่าสุด" },
        { key: "status", label: "สถานะ" },
        { key: "actions", label: "" },
      ],
    };
  },
  computed: {
    items() {
      return this.includeRevoked ? this.rawItems : this.rawItems.filter(item => item.active);
    },
  },
  created() {
    this.load();
  },
  methods: {
    formatDateTime(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "-";
    },
    async load() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        const data = await this.$store.dispatch("moduleMcpSetting/fetchConnections", {
          clinicUrl: this.clinicUrl,
          includeRevoked: true,
        });
        this.rawItems = data.items;
        this.$emit("loaded", data.items);
      } catch (error) {
        this.errorMessage = mcpErrorMessage(error, "โหลดรายการการเชื่อมต่อไม่สำเร็จ");
      } finally {
        this.isLoading = false;
      }
    },
    async confirmRevoke(connection) {
      const who = connection.user.name || connection.user.email || `uid ${connection.user.uid}`;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `ยกเลิกการเชื่อมต่อ ${connection.clientName || "แอป AI"} ของ ${who}? AI จะใช้ข้อมูลคลินิกไม่ได้ทันที และผู้ใช้ต้องเชื่อมต่อใหม่`,
        {
          title: "ยืนยันการยกเลิกการเชื่อมต่อ",
          okVariant: "danger",
          okTitle: "ยกเลิกการเชื่อมต่อ",
          cancelTitle: "ไม่ใช่",
          centered: true,
        }
      );
      if (!confirmed) return;
      this.revokingId = connection.id;
      this.errorMessage = "";
      try {
        await this.$store.dispatch("moduleMcpSetting/revokeConnection", {
          clinicUrl: this.clinicUrl,
          connectionId: connection.id,
        });
        await this.load();
      } catch (error) {
        const message = mcpErrorMessage(error, "ยกเลิกการเชื่อมต่อไม่สำเร็จ กรุณาลองใหม่");
        // The revoke may already have applied server-side even though this
        // request errored (e.g. a dropped response) — reload so the table
        // reflects the true state instead of showing a stale "active" row.
        // `load()` resets errorMessage, so re-apply it after the reload settles.
        await this.load();
        this.errorMessage = message;
      } finally {
        this.revokingId = null;
      }
    },
  },
};
</script>
